import { type QueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import {
  findHydratedConnectorTileById,
  hydratedConnectorsQueryOptions,
  type HydratedConnector,
} from "@/api/connectors/index";
import {
  dragonTileQueryOptions,
  framesPageQueryOptions,
} from "@/api/dragon-tiles";
import { FRAMES_PAGE_SIZE } from "@/components/TilesPagination/TilesPagination";
import { ParentTilePage } from "@/features/Connectors/ParentTilePage";
import { connectorReturnSearchSchema } from "@/features/DragonTiles/connectorTileSelectReturn";
import { getParentPoolInnerTile } from "@/features/DragonTiles/innerTileUtils";

const searchSchema = z
  .object({
    page: z
      .union([z.string(), z.number()])
      .transform((v) => (typeof v === "string" ? Number(v) : v))
      .pipe(z.number().int().positive())
      .catch(1)
      .optional(),
  })
  .merge(connectorReturnSearchSchema);

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/connectors/$parentTile",
)({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }) => [search],
  loader: async ({ params, context, deps: [search] }) => {
    const { queryClient } = context as { queryClient: QueryClient };
    const page = Math.max(1, search.page ?? 1);
    const { siteId, parentTile: connectorId } = params;

    const hydratedConnectors = await queryClient.ensureQueryData<
      HydratedConnector[]
    >(hydratedConnectorsQueryOptions(siteId, queryClient));
    const hydrated = findHydratedConnectorTileById(
      hydratedConnectors,
      connectorId,
    );
    if (!hydrated) {
      throw redirect({
        to: "/dragon-tiles/$siteId/connectors",
        params: { siteId },
      });
    }

    const dragonTile = await queryClient.ensureQueryData(
      dragonTileQueryOptions(siteId, hydrated.dragonTile.id),
    );
    const parentPoolInnerTileId = getParentPoolInnerTile(dragonTile)?.id;

    await queryClient.ensureQueryData(
      framesPageQueryOptions(siteId, page, FRAMES_PAGE_SIZE, {
        dragon_tile: dragonTile.id,
        ...(parentPoolInnerTileId ? { inner_tile: parentPoolInnerTileId } : {}),
        exclude_content_text: true,
      }),
    );
  },
});

function RouteComponent() {
  return <ParentTilePage />;
}
